import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MetaTags from "react-meta-tags";
import FeatherIcon from "feather-icons-react";
import { Spinner } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { changelayoutMode } from "../../store/actions";
import "./dashboard2.scss";

// After a scan starts, reload the dashboard once so finished results show up
const RESULTS_RELOAD_DELAY_MS = 60 * 1000;

// "11:59:42" (hh:mm:ss) until the given time
function formatTimeLeft(iso, now) {
  const totalSeconds = Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
}

// Live countdown kept in its own component so only this text re-renders every second
function RefreshCountdown({ until, onDone }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = () => {
      const current = Date.now();
      if (new Date(until).getTime() <= current) onDone();
      else setNow(current);
    };
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [until, onDone]);

  return <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatTimeLeft(until, now)}</span>;
}

const Dashboard2 = () => {
  const dispatch = useDispatch();
  const { layoutMode } = useSelector(state => ({
    layoutMode: state.Layout.layoutMode,
  }));

  // Detect dark mode: synchronized with global app mode (header icon & redux)
  const isDark = layoutMode === "dark" || document.body.getAttribute("data-layout-mode") === "dark";
  const theme = isDark ? "dark" : "light";

  const [critical, setCritical] = useState(0);
  const [high, setHigh] = useState(0);
  const [medium, setMedium] = useState(0);
  const [low, setLow] = useState(0);
  const [informational, setInformational] = useState(0);

  const [expCredential, setExpCredential] = useState([]);
  const [expSubdomains, setExpSubdomains] = useState([]);
  const [desiredDomainInfo, setDesiredDomainInfo] = useState({});
  const [setA, setSetA] = useState([]);
  const [setAAAA, setSetAAAA] = useState([]);
  const [setCNAME, setSetCNAME] = useState([]);
  const [setSOA, setSetSOA] = useState([]);
  const [setMX, setSetMX] = useState([]);
  const [setNS, setSetNS] = useState([]);
  const [setTXT, setSetTXT] = useState([]);
  const [setCAA, setSetCAA] = useState([]);
  const [expPorts, setExpPorts] = useState([]);
  const [expReverseDNS, setExpReverseDNS] = useState([]);
  const [expReverseIP, setExpReverseIP] = useState([]);
  const [expBlackBox, setExpBlackBox] = useState([]);

  const [orgAsset_Domain, setOrgAsset_Domain] = useState([]);
  const [orgAsset_IP, setOrgAsset_IP] = useState([]);
  const [orgAsset_Subdomain, setOrgAsset_Subdomain] = useState([]);
  const [orgAsset_MobileApplication, setOrgAsset_MobileApplication] = useState([]);
  const [siteTitle, setSiteTitle] = useState(() => localStorage.getItem("domain") || "");
  const [loading, setLoading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportFeedback, setReportFeedback] = useState(null);
  // Refresh = start a new scan of all features, then lock until nextRefreshAt
  const [nextRefreshAt, setNextRefreshAt] = useState(null);
  const [startingScan, setStartingScan] = useState(false);
  const reloadTimer = useRef(null);
  const [activeTab, setActiveTab] = useState("asset");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const getPageNumbers = (current, total) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [];
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("...");
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push("...");
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      pages.push(current - 1);
      pages.push(current);
      pages.push(current + 1);
      pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  const token = localStorage.getItem("token");
  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID") || "179067062101800015959";

  const exposedCredentials = 165112129704651;
  const exposedSubdomains = 165112129704687;
  const exposedWHOIS = 165112129704697;
  const exposedDNS = 165112129704677;
  const exposedPorts = 165112129704796;
  const exposedReverseDNS = 165112129704189;
  const exposedReverseIP = 165112129704642;

  useEffect(() => {
    async function checkAuthAndLoad() {
      try {
        if (token) {
          const authRes = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const authData = await authRes.json();
          if (authData && authData.status === false) {
            window.location.href = "/login";
            return;
          }
        }
      } catch (err) {
        console.warn("JWT check error:", err);
      }
      getRiskCalculated();
      loadRefreshStatus();
    }
    checkAuthAndLoad();
  }, []);

  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  const refreshLocked = Boolean(nextRefreshAt);
  const unlockRefresh = useCallback(() => setNextRefreshAt(null), []);

  function refreshRequest(path) {
    return fetch(`${process.env.REACT_APP_DEFAULTPATH}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgID }),
    });
  }

  function applyRefreshStatus(status) {
    setNextRefreshAt(status && !status.canRefresh ? status.nextAllowedAt : null);
  }

  async function loadRefreshStatus() {
    try {
      const response = await refreshRequest("dashboard/refresh-status");
      const result = await response.json();
      if (result.status === "ok") applyRefreshStatus(result.data);
    } catch (err) {
      console.warn("Refresh status error:", err);
    }
  }

  async function handleRefresh() {
    setStartingScan(true);
    setReportFeedback(null);
    try {
      const response = await refreshRequest("dashboard/refresh");
      const result = await response.json();
      if (result.status === "ok") {
        applyRefreshStatus(result.data);
        setReportFeedback({
          type: "success",
          message: "Scan started for all features. New results will appear in a few minutes.",
        });
        clearTimeout(reloadTimer.current);
        reloadTimer.current = setTimeout(getRiskCalculated, RESULTS_RELOAD_DELAY_MS);
      } else if (result.status === "cooldown") {
        applyRefreshStatus(result.data);
      } else {
        setReportFeedback({ type: "danger", message: result.message || "Could not start the scan. Please try again." });
      }
    } catch (err) {
      setReportFeedback({ type: "danger", message: "Could not start the scan. Please try again." });
    } finally {
      setStartingScan(false);
    }
  }

  async function getRiskCalculated() {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}riskcalculator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userID,
          orgID,
          exposedCredentials,
          exposedSubdomains,
          exposedWHOIS,
          exposedDNS,
          exposedPorts,
          exposedReverseDNS,
          exposedReverseIP,
        }),
      });
      const resData = await response.json();
      if (resData && resData.data) {
        const d = resData.data;

        if (d.riskScore) {
          setCritical(d.riskScore.Critical || 0);
          setHigh(d.riskScore.High || 0);
          setMedium(d.riskScore.Medium || 0);
          setLow(d.riskScore.Low || 0);
          setInformational(d.riskScore.Informational || 0);
        }

        if (d.orgAsset) {
          setOrgAsset_Domain(d.orgAsset.Domain || []);
          setOrgAsset_IP(d.orgAsset.IP || []);
          setOrgAsset_Subdomain(d.orgAsset.Subdomain || []);
          setOrgAsset_MobileApplication(d.orgAsset.MobileApplication || []);

          const PUBLIC_PROVIDERS = [
            "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com",
            "aol.com", "icloud.com", "mail.com", "protonmail.com", "zoho.com",
            "yandex.com", "gmx.com", "fastmail.com", "tutanota.com"
          ];
          let foundName = "";
          const domains = d.orgAsset.Domain || [];
          const assets = d.orgAsset.Asset || [];
          for (const item of domains) {
            const val = item.Domain || item.Value;
            if (val && !PUBLIC_PROVIDERS.includes(val.toLowerCase())) {
              foundName = val;
              break;
            }
          }
          if (!foundName) {
            for (const item of assets) {
              if (item.Type === "Domain" && item.Value && !PUBLIC_PROVIDERS.includes(item.Value.toLowerCase())) {
                foundName = item.Value;
                break;
              }
            }
          }
          if (!foundName && domains.length > 0) {
            foundName = domains[0]?.Domain || domains[0]?.Value;
          }
          if (foundName) {
            setSiteTitle(foundName);
            localStorage.setItem("domain", foundName);
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("domainChanged"));
            }
          }
        }

        let portsList = [];
        let ripList = [];

        if (d.exposedCredentials && Array.isArray(d.exposedCredentials.Data)) {
          setExpCredential(d.exposedCredentials.Data);
        }
        if (d.exposedSubdomains && Array.isArray(d.exposedSubdomains.Data)) {
          setExpSubdomains(d.exposedSubdomains.Data);
        }

        if (d.exposedPorts && Array.isArray(d.exposedPorts.Data)) {
          portsList = [];
          d.exposedPorts.Data.forEach(item => {
            if (Array.isArray(item.Ports)) {
              item.Ports.forEach(p => {
                portsList.push({
                  ID: item.ID,
                  IP: item.IP,
                  Port: p.Port,
                  Service: p.Service,
                  Risk: p.Risk,
                });
              });
            }
          });
          setExpPorts(portsList);
        }

        if (d.exposedWHOIS && Array.isArray(d.exposedWHOIS.Data) && d.exposedWHOIS.Data.length > 0) {
          setDesiredDomainInfo(d.exposedWHOIS.Data[0]);
        }

        if (d.exposedDNS && Array.isArray(d.exposedDNS.Data) && d.exposedDNS.Data.length > 0) {
          const dnsObj = d.exposedDNS.Data[0];
          setSetA(dnsObj.A || []);
          setSetAAAA(dnsObj.AAAA || []);
          setSetCNAME(dnsObj.CNAME || []);
          setSetSOA(dnsObj.SOA || []);
          setSetMX(dnsObj.MX || []);
          setSetNS(dnsObj.NS || []);
          setSetTXT(dnsObj.TXT || []);
          setSetCAA(dnsObj.CAA || []);
        } else {
          setSetA([]);
          setSetAAAA([]);
          setSetCNAME([]);
          setSetSOA([]);
          setSetMX([]);
          setSetNS([]);
          setSetTXT([]);
          setSetCAA([]);
        }

        if (d.exposedReverseDNS && Array.isArray(d.exposedReverseDNS.Data)) {
          setExpReverseDNS(d.exposedReverseDNS.Data);
        }

        if (d.exposedReverseIP && Array.isArray(d.exposedReverseIP.Data)) {
          ripList = [];
          d.exposedReverseIP.Data.forEach(item => {
            if (Array.isArray(item.Domains)) {
              item.Domains.forEach(dom => {
                ripList.push({
                  ID: item.ID,
                  IP: item.IP,
                  Domain: dom.Domain,
                  Risk: dom.Risk,
                });
              });
            }
          });
          setExpReverseIP(ripList);
        }

        if (d.exposedBlackBox && d.exposedBlackBox.Data && Array.isArray(d.exposedBlackBox.Data.findings)) {
          setExpBlackBox(d.exposedBlackBox.Data.findings);
        }

        // Cache real counts for sidebar and trigger event
        try {
          let dnsCountCalc = 0;
          if (d.exposedDNS && Array.isArray(d.exposedDNS.Data)) {
            d.exposedDNS.Data.forEach(item => {
              ['A', 'AAAA', 'CNAME', 'SOA', 'MX', 'NS', 'TXT', 'CAA'].forEach(recType => {
                if (Array.isArray(item[recType])) dnsCountCalc += item[recType].length;
              });
            });
            if (dnsCountCalc === 0) dnsCountCalc = d.exposedDNS.Data.length;
          }
          const sidebarCounts = {
            credentials: (d.exposedCredentials && Array.isArray(d.exposedCredentials.Data)) ? d.exposedCredentials.Data.length : 0,
            subdomains: (d.exposedSubdomains && Array.isArray(d.exposedSubdomains.Data)) ? d.exposedSubdomains.Data.length : 0,
            whois: (d.exposedWHOIS && Array.isArray(d.exposedWHOIS.Data)) ? d.exposedWHOIS.Data.length : 0,
            dns: dnsCountCalc,
            ports: portsList.length,
            reverseDns: (d.exposedReverseDNS && Array.isArray(d.exposedReverseDNS.Data)) ? d.exposedReverseDNS.Data.length : 0,
            reverseIp: ripList.length,
          };
          localStorage.setItem("exposureCounts", JSON.stringify(sidebarCounts));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("exposureCountsChanged"));
          }
        } catch (e) {}
      }
    } catch (err) {
      console.warn("Failed to fetch risk calculation:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (reportFeedback) {
      const timer = setTimeout(() => setReportFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [reportFeedback]);

  // Generate and Download Consolidated ASM PDF Report
  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    setReportFeedback(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}reports/generate?orgID=${encodeURIComponent(orgID)}&format=pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );

      if (!response.ok) {
        let errMsg = "Failed to generate report";
        try {
          const errJson = await response.json();
          if (errJson.error) errMsg = errJson.error;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const disposition = response.headers.get("content-disposition");
      let filename = `ASM_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (match && match[1]) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setReportFeedback({ type: "success", message: "ASM PDF Report downloaded successfully." });
    } catch (err) {
      console.error("Report generation error:", err);
      setReportFeedback({ type: "danger", message: err.message || "Failed to generate ASM report" });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Consistent findings counts matching exact backend data
  const countCrit = critical;
  const countHigh = high;
  const countMed = medium;
  const countLow = low;
  const countInfo = informational;
  const totalFindings = countCrit + countHigh + countMed + countLow + countInfo;

  const severities = useMemo(() => [
    { key: "critical",  label: "Critical",      short: "Crit", count: countCrit, color: "#dc3b2c" },
    { key: "high",      label: "High",          short: "High", count: countHigh, color: "#e2670f" },
    { key: "medium",    label: "Medium",        short: "Med",  count: countMed,  color: "#d99a1c" },
    { key: "low",       label: "Low",           short: "Low",  count: countLow,  color: "#4a86f5" },
    { key: "info",      label: "Informational", short: "Info", count: countInfo, color: "#8b5cf6" },
  ], [countCrit, countHigh, countMed, countLow, countInfo]);

  // Organization Score & Severity: dynamically calculated from findings
  // Based on cybersecurity ASM industry standards (NIST / CVSS / SecurityScorecard):
  // 1. Any Critical findings -> Severity is Critical (Score 15-39)
  // 2. High findings (no Crit) -> Severity is High (Score 40-59)
  // 3. Medium findings (no Crit/High) -> Severity is Medium (Score 60-84)
  // 4. Low/Info findings -> Severity is Low (Score 85-100)
  const currentSeverityKey = useMemo(() => {
    if (countCrit > 0) return "critical";
    if (countHigh > 0) return "high";
    if (countMed > 0) return "medium";
    if (countLow > 0) return "low";
    return "info";
  }, [countCrit, countHigh, countMed, countLow]);

  const orgScore = useMemo(() => {
    if (totalFindings === 0) return 100;
    const deduction = countCrit * 12 + countHigh * 6 + countMed * 2 + countLow * 1;
    const rawScore = 100 - deduction;

    if (countCrit > 0) {
      return Math.max(15, Math.min(39, rawScore));
    } else if (countHigh > 0) {
      return Math.max(40, Math.min(59, rawScore));
    } else if (countMed > 0) {
      return Math.max(60, Math.min(84, rawScore));
    } else {
      return Math.max(85, Math.min(100, rawScore));
    }
  }, [totalFindings, countCrit, countHigh, countMed, countLow]);

  const currentSeverityLabel = currentSeverityKey === "critical" ? "Critical"
    : currentSeverityKey === "high" ? "High"
    : currentSeverityKey === "medium" ? "Medium"
    : currentSeverityKey === "low" ? "Low" : "Informational";

  const currentSeverityColor = `var(--sev-${currentSeverityKey})`;

  const levelSegments = useMemo(() => {
    return severities.map(s => ({
      color: s.color,
      opacity: s.key === currentSeverityKey ? 1 : 0.25,
    }));
  }, [severities, currentSeverityKey]);

  // Organization Assets calculation: Deduplicated by Type + Value so identical entries are removed
  const resolvedAssets = useMemo(() => {
    const list = [];
    if (orgAsset_Domain.length > 0) {
      orgAsset_Domain.forEach(d => list.push({ ID: d.ID, Type: d.Type || "Domain", Value: d.Value || d.Domain, Status: d.Status || "Verified" }));
    }
    if (orgAsset_IP.length > 0) {
      orgAsset_IP.forEach(ip => list.push({ ID: ip.ID, Type: ip.Type || "IP", Value: ip.Value || ip.IP, Status: ip.Status || "Verified" }));
    }
    if (orgAsset_Subdomain.length > 0) {
      orgAsset_Subdomain.forEach(sub => list.push({ ID: sub.ID, Type: sub.Type || "Subdomain", Value: sub.Value || sub.Subdomain, Status: sub.Status || "Verified" }));
    }
    if (orgAsset_MobileApplication.length > 0) {
      orgAsset_MobileApplication.forEach(m => list.push({ ID: m.ID, Type: m.Type || "MobileApplication", Value: m.Value || m.Name, Status: m.Status || "Verified" }));
    }

    const source = list;
    const seen = new Set();
    const unique = [];
    source.forEach(item => {
      const key = `${item.Type}_${(item.Value || "").trim().toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    });
    return unique;
  }, [orgAsset_Domain, orgAsset_IP, orgAsset_Subdomain, orgAsset_MobileApplication]);

  const domainCount = resolvedAssets.filter(a => a.Type === "Domain").length;
  const ipCount = resolvedAssets.filter(a => a.Type === "IP").length;
  const subCount = resolvedAssets.filter(a => a.Type === "Subdomain").length;
  const totalAssetsCount = resolvedAssets.length;

  // Donut Arc calculation (SVG) — balanced so non-zero severities have clearly visible slices
  const [hoveredArc, setHoveredArc] = useState(null);
  const svgRef = React.useRef(null);

  const pieArcs = useMemo(() => {
    const pt = (r, deg) => {
      const rad = ((deg - 90) * Math.PI) / 180;
      return [100 + r * Math.cos(rad), 100 + r * Math.sin(rad)];
    };
    const arc = (r, a0, a1) => {
      if (a1 <= a0) return "";
      const [x0, y0] = pt(r, a0);
      const [x1, y1] = pt(r, a1);
      const largeArc = a1 - a0 > 180 ? 1 : 0;
      return `M${x0} ${y0} A${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`;
    };

    const active = severities.filter(s => s.count > 0);
    const numActive = active.length;

    if (totalFindings === 0 || numActive === 0) {
      return severities.map(s => ({
        d: "",
        color: s.color,
        tx: 0,
        ty: 0,
        startAngle: 0,
        endAngle: 0,
        sw: 0,
      }));
    }

    // Minimum visible angle in degrees for any active severity slice (15 deg)
    const minAngle = numActive > 1 ? 15 : 360;
    const reserved = numActive > 1 ? numActive * minAngle : 0;
    const pool = numActive > 1 ? Math.max(0, 360 - reserved) : 0;

    let a = 0;
    return severities.map(s => {
      if (s.count === 0) {
        return {
          d: "",
          color: s.color,
          tx: 0,
          ty: 0,
          startAngle: a,
          endAngle: a,
          sw: 0,
        };
      }

      const sw = numActive === 1
        ? 360
        : minAngle + (s.count / totalFindings) * pool;

      const startAngle = a;
      const endAngle = a + sw;

      let d = "";
      if (numActive === 1) {
        d = `${arc(76, 0.1, 180)} ${arc(76, 180, 359.9)}`;
      } else {
        const gap = 1.5;
        d = arc(76, startAngle + gap, endAngle - gap);
      }

      const midAngle = a + sw / 2;
      a += sw;

      const midRad = ((midAngle - 90) * Math.PI) / 180;
      const tx = Math.cos(midRad) * 6;
      const ty = Math.sin(midRad) * 6;

      return { d, color: s.color, tx, ty, startAngle, endAngle, sw };
    });
  }, [severities, totalFindings]);

  // SVG-level mouse tracking — avoids flicker when hovering between arc gaps
  const handleSvgMouseMove = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // Map screen coords to SVG viewBox coords (200x200, center=100,100)
    const scaleX = 200 / rect.width;
    const scaleY = 200 / rect.height;
    const x = (e.clientX - rect.left) * scaleX - 100;
    const y = (e.clientY - rect.top) * scaleY - 100;
    const dist = Math.sqrt(x * x + y * y);
    // Only detect hover within donut ring (radius 58–94 px in viewBox)
    if (dist < 58 || dist > 94) {
      setHoveredArc(null);
      return;
    }
    // Convert to angle 0–360 starting from top (like our arc math)
    let angle = (Math.atan2(y, x) * 180) / Math.PI + 90;
    if (angle < 0) angle += 360;
    // Find which arc segment this angle belongs to
    const idx = pieArcs.findIndex(
      arc => arc.sw > 0 && angle >= arc.startAngle && angle < arc.endAngle
    );
    setHoveredArc(idx >= 0 ? idx : null);
  }, [pieArcs]);

  // Balanced Findings Bar segments so non-zero severities are clearly visible and not hidden
  const findingsBarSegments = useMemo(() => {
    if (totalFindings === 0) return [];
    const active = severities.filter(s => s.count > 0);
    const numActive = active.length;
    if (numActive === 0) return [];
    if (numActive === 1) {
      return active.map(s => ({ ...s, pct: 100 }));
    }

    const minPct = 7;
    const reserved = numActive * minPct;
    const pool = Math.max(0, 100 - reserved);

    return active.map(s => {
      const pct = minPct + (s.count / totalFindings) * pool;
      return { ...s, pct };
    });
  }, [severities, totalFindings]);

  // Risk Chart-2 Matrix Rows: Includes Black Box so the 1 Critical and 2 Medium findings are visible
  const matrixData = useMemo(() => {
    const calcCounts = (arr, field = "Risk") => {
      const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      if (!Array.isArray(arr)) return counts;
      arr.forEach(item => {
        const r = String(item[field] || item.Severity || "").toLowerCase();
        if (r === "critical") counts.critical++;
        else if (r === "high") counts.high++;
        else if (r === "medium") counts.medium++;
        else if (r === "low") counts.low++;
        else if (r === "informational" || r === "info") counts.info++;
      });
      return counts;
    };

    const credCounts = calcCounts(expCredential);
    const subCounts = calcCounts(expSubdomains);
    const whoisCounts = desiredDomainInfo?.Risk ? calcCounts([desiredDomainInfo]) : calcCounts([]);

    const dnsAll = [...setA, ...setAAAA, ...setCNAME, ...setSOA, ...setMX, ...setNS, ...setTXT, ...setCAA];
    const dnsCounts = calcCounts(dnsAll);

    const portsCounts = calcCounts(expPorts);
    const rdnsCounts = calcCounts(expReverseDNS);
    const ripCounts = calcCounts(expReverseIP);

    const rawRows = [
      { label: "Credentials", counts: credCounts },
      { label: "Subdomains", counts: subCounts },
      { label: "WHOIS", counts: whoisCounts },
      { label: "DNS", counts: dnsCounts },
      { label: "Ports", counts: portsCounts },
      { label: "ReverseDNS", counts: rdnsCounts },
      { label: "ReverseIP", counts: ripCounts },
    ];

    const tintMap = {
      critical: { bg: "#fbdedb", fg: "#c0281c" },
      high:     { bg: "#fce3cf", fg: "#c2540a" },
      medium:   { bg: "#faedcf", fg: "#a86a05" },
      low:      { bg: "#dce7fd", fg: "#1e4fc4" },
      info:     { bg: "#ede9fe", fg: "#6d28d9" },
    };

    return rawRows.map(row => ({
      label: row.label,
      cells: severities.map(sev => {
        const val = row.counts[sev.key] || 0;
        return val > 0
          ? { text: val, bg: tintMap[sev.key].bg, fg: tintMap[sev.key].fg }
          : { text: "·", bg: "var(--bg-surface-muted)", fg: "var(--fg-tertiary)" };
      }),
    }));
  }, [expCredential, expSubdomains, desiredDomainInfo, setA, setAAAA, setCNAME, setSOA, setMX, setNS, setTXT, setCAA, expPorts, expReverseDNS, expReverseIP, severities]);

  // Tab views mapping: Includes the 7 ASM features
  const tabViews = useMemo(() => {
    const whoisRows = [
      { Field: "Domain", Value: desiredDomainInfo.Domain || siteTitle || "-" },
      { Field: "Name", Value: desiredDomainInfo.Registrar_Details?.Registrar_Name || desiredDomainInfo.Name || "-" },
      { Field: "Organization", Value: desiredDomainInfo.Registrar_Details?.Registrar_Organization || desiredDomainInfo.Organization || "-" },
      { Field: "IANA ID", Value: desiredDomainInfo.Registrar_Details?.Registrar_IANA_ID || desiredDomainInfo.IANA_ID || "-" },
      { Field: "WHOIS Server", Value: desiredDomainInfo.Registrar_Details?.Registrar_WHOIS_Server || desiredDomainInfo.WHOIS_Server || "-" },
      { Field: "URL", Value: desiredDomainInfo.Registrar_Details?.Registrar_URL || desiredDomainInfo.URL || "-" },
    ];

    const resolvedSubdomains = expSubdomains;
    const resolvedPorts = expPorts;

    // DNS mapping: properly handle all DNS record types
    const dnsItems = [];
    if (setA.length > 0) {
      setA.forEach(item => dnsItems.push({ ID: item.ID, Type: "A", Data: item.IPv4 || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setAAAA.length > 0) {
      setAAAA.forEach(item => dnsItems.push({ ID: item.ID, Type: "AAAA", Data: item.IPv6 || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setCNAME.length > 0) {
      setCNAME.forEach(item => dnsItems.push({ ID: item.ID, Type: "CNAME", Data: item.Target || item.HostName || item.Data || item.Value || "-", Risk: item.Risk || "Informational" }));
    }
    if (setSOA.length > 0) {
      setSOA.forEach(item => dnsItems.push({ ID: item.ID, Type: "SOA", Data: item.HostMaster || item.AdminEmail || (item.Serial ? `Serial: ${item.Serial}` : "-") || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setMX.length > 0) {
      setMX.forEach(item => dnsItems.push({ ID: item.ID, Type: "MX", Data: item.Target || item.HostName || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setNS.length > 0) {
      setNS.forEach(item => dnsItems.push({ ID: item.ID, Type: "NS", Data: item.Target || item.HostName || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setTXT.length > 0) {
      setTXT.forEach(item => dnsItems.push({ ID: item.ID, Type: "TXT", Data: item.Value || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    if (setCAA.length > 0) {
      setCAA.forEach(item => dnsItems.push({ ID: item.ID, Type: "CAA", Data: item.Value || item.Tag || item.Data || "-", Risk: item.Risk || "Informational" }));
    }
    const resolvedDNS = dnsItems;

    const resolvedReverseDNS = expReverseDNS;
    const resolvedReverseIP = expReverseIP;

    return {
      asset: {
        id: "asset",
        label: "Organization Asset",
        title: "Organization Asset",
        cols: ["ID", "Type", "Value", "Status"],
        rows: resolvedAssets.map(a => [
          { text: a.ID, isId: true },
          { text: a.Type },
          { text: a.Value, isMono: true },
          { text: a.Status || "Verified", badge: true, sev: "success" },
        ]),
      },
      cred: {
        id: "cred",
        label: "Credentials",
        title: "Top Risk of Exposed Credentials",
        cols: ["ID", "Email", "Risk"],
        rows: expCredential.map(c => [
          { text: c.ID, isId: true },
          { text: c.Email, isMono: true },
          { text: c.Risk, badge: true, sev: c.Risk?.toLowerCase() },
        ]),
      },
      sub: {
        id: "sub",
        label: "Subdomains",
        title: "Top Risk of Exposed Subdomains",
        cols: ["ID", "Subdomain", "Risk"],
        rows: resolvedSubdomains.map(s => [
          { text: s.ID, isId: true },
          { text: s.Subdomain, isMono: true },
          { text: s.Risk || "Informational", badge: true, sev: (s.Risk || "info").toLowerCase() },
        ]),
      },
      whois: {
        id: "whois",
        label: "WHOIS",
        title: "Top Risk of Exposed WHOIS",
        sub: "DOMAIN INFORMATION",
        cols: ["Field", "Value"],
        rows: whoisRows.map(w => [
          { text: w.Field },
          { text: w.Value, isMono: true },
        ]),
      },
      dns: {
        id: "dns",
        label: "DNS",
        title: "Top Risk of Exposed DNS",
        sub: "A",
        cols: ["ID", "Type", "Data", "Risk"],
        rows: resolvedDNS.map(d => [
          { text: d.ID || "-", isId: true },
          { text: d.Type || "A", isMono: true },
          { text: d.Data || "-", isMono: true },
          { text: d.Risk || "Informational", badge: true, sev: (d.Risk || "info").toLowerCase() },
        ]),
      },
      ports: {
        id: "ports",
        label: "Ports",
        title: "Top Risk of Exposed Ports",
        cols: ["ID", "IP", "Port", "Service", "Risk"],
        rows: resolvedPorts.map(p => [
          { text: p.ID, isId: true },
          { text: p.IP, isMono: true },
          { text: p.Port, isMono: true },
          { text: p.Service, isMono: true },
          { text: p.Risk, badge: true, sev: p.Risk?.toLowerCase() },
        ]),
      },
      rdns: {
        id: "rdns",
        label: "ReverseDNS",
        title: "Top Risk of Exposed ReverseDNS",
        cols: ["ID", "Domain", "Risk"],
        rows: resolvedReverseDNS.map(rd => [
          { text: rd.ID, isId: true },
          { text: rd.Domain, isMono: true },
          { text: rd.Risk || "Informational", badge: true, sev: (rd.Risk || "info").toLowerCase() },
        ]),
      },
      rip: {
        id: "rip",
        label: "ReverseIP",
        title: "Top Risk of Exposed ReverseIP",
        cols: ["ID", "IP", "Domain", "Risk"],
        rows: resolvedReverseIP.map(rip => [
          { text: rip.ID, isId: true },
          { text: rip.IP, isMono: true },
          { text: rip.Domain, isMono: true },
          { text: rip.Risk || "Informational", badge: true, sev: (rip.Risk || "info").toLowerCase() },
        ]),
      },
    };
  }, [resolvedAssets, expCredential, expSubdomains, desiredDomainInfo, siteTitle, setA, setAAAA, setCNAME, setSOA, setMX, setNS, setTXT, setCAA, expPorts, expReverseDNS, expReverseIP]);

  const currentView = tabViews[activeTab] || tabViews.asset;
  const totalRows = currentView.rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);
  const paginatedRows = currentView.rows.slice(startIndex, endIndex);


  const getBadgeClass = (sev) => {
    switch (String(sev).toLowerCase()) {
      case "critical":
        return "asm-badge badge-critical";
      case "high":
        return "asm-badge badge-high";
      case "medium":
      case "med":
        return "asm-badge badge-medium";
      case "low":
        return "asm-badge badge-low";
      case "informational":
      case "info":
        return "asm-badge badge-info";
      case "active":
      case "success":
      case "verified":
        return "asm-badge badge-success";
      default:
        return "asm-badge badge-info";
    }
  };

  return (
    <React.Fragment>
      <MetaTags>
        <title>Dashboard | Glogix</title>
      </MetaTags>

      <div className="page-content asm-dashboard-root" data-theme={theme}>
        <div className="asm-dashboard-content">

          {/* Header Row: Breadcrumb, Title with Status Pill, Refresh & Theme Toggles */}
          <div className="asm-header-row">
            <div className="asm-header-info">
              <div className="asm-breadcrumbs">
                <span>Dashboard</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span style={{ color: "var(--fg-primary)" }}>Dashboard</span>
              </div>
              <div className="asm-title-row">
                <h1 className="asm-main-title">Dashboard</h1>
                <div className="asm-site-pill">
                  <span className="asm-status-dot" />
                  <span>{siteTitle?.startsWith("www.") ? siteTitle : `www.${siteTitle || "example.com"}`}</span>
                  <span style={{ color: "var(--fg-tertiary)" }}>·</span>
                  <span style={{ color: "var(--amber-500)", fontWeight: 600 }}>302</span>
                </div>
              </div>
            </div>

            <div className="asm-actions-group">
              <button
                className="asm-btn-report"
                onClick={handleGenerateReport}
                disabled={isGeneratingReport}
                title="Generate Consolidated ASM Report (PDF)"
              >
                {isGeneratingReport ? (
                  <>
                    <Spinner size="sm" color="light" />
                    <span>Generating Report...</span>
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="file-text" size={14} />
                    <span>Generate Report</span>
                  </>
                )}
              </button>
              <button
                className="asm-btn-refresh"
                onClick={handleRefresh}
                disabled={loading || startingScan || refreshLocked}
                title={refreshLocked ? `Available at ${new Date(nextRefreshAt).toLocaleString()}` : undefined}
              >
                {startingScan ? (
                  <>
                    <Spinner size="sm" color="light" />
                    <span>Starting scan...</span>
                  </>
                ) : loading ? (
                  <>
                    <Spinner size="sm" color="light" />
                    <span>Refreshing...</span>
                  </>
                ) : refreshLocked ? (
                  <>
                    <FeatherIcon icon="clock" size={14} />
                    <span>
                      Next refresh in <RefreshCountdown until={nextRefreshAt} onDone={unlockRefresh} />
                    </span>
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="refresh-cw" size={14} />
                    <span>Refresh</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Report Feedback Notification */}
          {reportFeedback && (
            <div className={`asm-report-alert asm-report-alert-${reportFeedback.type}`}>
              <FeatherIcon icon={reportFeedback.type === "success" ? "check-circle" : "alert-circle"} size={16} />
              <span>{reportFeedback.message}</span>
              <button onClick={() => setReportFeedback(null)} className="asm-alert-close" type="button">&times;</button>
            </div>
          )}

          {/* Top KPI Bar (4 metrics in unified bordered card) */}
          <div className="asm-kpi-grid">
            {/* 1. Overall Organization Score */}
            <div className="asm-kpi-cell">
              <div className="asm-kpi-label">Overall Organization Score</div>
              <div className="asm-kpi-value-row">
                <span className="asm-kpi-value-big">{orgScore}</span>
                <span className="asm-kpi-value-sub">/ 100</span>
              </div>
              <div className="asm-kpi-progress-track">
                <div className="asm-kpi-progress-bar" style={{ width: `${orgScore}%` }} />
              </div>
            </div>

            {/* 2. Overall Organization Severity */}
            <div className="asm-kpi-cell">
              <div className="asm-kpi-label">Overall Organization Severity</div>
              <div className="asm-kpi-value-row">
                <span
                  className="asm-kpi-value-big"
                  style={{ color: currentSeverityColor }}
                >
                  {currentSeverityLabel}
                </span>
              </div>
              <div className="asm-kpi-level-bars">
                {levelSegments.map((seg, idx) => (
                  <div
                    key={idx}
                    className="asm-kpi-level-segment"
                    style={{ background: seg.color, opacity: seg.opacity }}
                  />
                ))}
              </div>
            </div>

            {/* 3. Findings */}
            <div className="asm-kpi-cell">
              <div className="asm-kpi-label">Findings</div>
              <div className="asm-kpi-value-row">
                <span className="asm-kpi-value-big">{totalFindings}</span>
              </div>
              <div className="asm-kpi-stacked-bar">
                {findingsBarSegments.map((sev) => (
                  <div
                    key={sev.key}
                    title={`${sev.label}: ${sev.count} (${((sev.count / totalFindings) * 100).toFixed(1)}%)`}
                    style={{ width: `${sev.pct}%`, background: sev.color }}
                  />
                ))}
              </div>
            </div>

            {/* 4. Organization Asset */}
            <div className="asm-kpi-cell">
              <div className="asm-kpi-label">Organization Asset</div>
              <div className="asm-kpi-value-row">
                <span className="asm-kpi-value-big">{totalAssetsCount}</span>
              </div>
              <div className="asm-kpi-asset-pills">
                <span>Domain <b>{domainCount}</b></span>
                <span>IP <b>{ipCount}</b></span>
                <span>Subdomain <b>{subCount}</b></span>
              </div>
            </div>
          </div>

          {/* Middle Row: 3 Cards (Site Information, Risk Chart-1, Risk Chart-2) */}
          <div className="asm-cards-grid">
            {/* Card 1: Site Information */}
            <div className="asm-card">
              <div className="asm-card-header">Site Information</div>
              <div className="asm-site-info-list">
                <div className="asm-site-info-row">
                  <span className="asm-site-info-key">Site</span>
                  <span className="asm-site-info-val">www.{siteTitle}</span>
                </div>
                <div className="asm-site-info-row">
                  <span className="asm-site-info-key">Title</span>
                  <span className="asm-site-info-val mono">www.{siteTitle}</span>
                </div>
                <div className="asm-site-info-row">
                  <span className="asm-site-info-key">HTTP Status</span>
                  <span className="asm-site-info-val">
                    <span className="asm-http-status-pill">302</span>
                  </span>
                </div>
                <div className="asm-site-info-row">
                  <span className="asm-site-info-key">URL</span>
                  <span className="asm-site-info-val">
                    <a href={`http://www.${siteTitle}`} target="_blank" rel="noreferrer">
                      www.{siteTitle}
                    </a>
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Risk Chart-1 (Donut Chart) */}
            <div className="asm-card">
              <div className="asm-card-header">Risk Chart-1</div>
              <div className="asm-donut-wrapper">
                <div className="asm-donut-chart-box">
                  <svg
                    ref={svgRef}
                    width="150" height="150" viewBox="0 0 200 200"
                    style={{ overflow: "visible", cursor: "default" }}
                    onMouseMove={handleSvgMouseMove}
                    onMouseLeave={() => setHoveredArc(null)}
                  >
                    <defs>
                      {pieArcs.map((arc, i) => (
                        <filter key={`glow-${i}`} id={`arc-glow-${i}`} x="-30%" y="-30%" width="160%" height="160%">
                          <feGaussianBlur stdDeviation="2.5" result="blur" />
                          <feFlood floodColor={arc.color} floodOpacity="0.45" result="color" />
                          <feComposite in="color" in2="blur" operator="in" result="glow" />
                          <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      ))}
                    </defs>

                    <circle
                      cx="100"
                      cy="100"
                      r="76"
                      fill="none"
                      stroke="var(--slate-800)"
                      strokeWidth="26"
                      style={{ opacity: 0.35 }}
                    />

                    {pieArcs.map((arc, i) => (
                      <path
                        key={i}
                        d={arc.d}
                        fill="none"
                        stroke={arc.color}
                        strokeWidth="26"
                        style={{
                          transform: hoveredArc === i
                            ? `translate(${arc.tx}px, ${arc.ty}px)`
                            : "translate(0,0)",
                          transition: "transform 0.25s cubic-bezier(0.25,1.2,0.5,1), filter 0.2s ease",
                          filter: hoveredArc === i ? `url(#arc-glow-${i})` : "none",
                          pointerEvents: "none",
                        }}
                      />
                    ))}
                  </svg>

                  <div className="asm-donut-center-text">
                    <span
                      className="asm-donut-total"
                      style={{
                        color: hoveredArc !== null ? severities[hoveredArc]?.color : undefined,
                        transition: "color 0.18s ease",
                      }}
                    >
                      {hoveredArc !== null ? severities[hoveredArc]?.count : totalFindings}
                    </span>
                    <span className="asm-donut-sub">
                      {hoveredArc !== null ? severities[hoveredArc]?.label : "findings"}
                    </span>
                  </div>
                </div>

                <div className="asm-donut-legend">
                  {severities.map((s, i) => (
                    <div
                      key={s.key}
                      className="asm-legend-row"
                      onMouseEnter={() => setHoveredArc(i)}
                      onMouseLeave={() => setHoveredArc(null)}
                      style={{
                        cursor: "pointer",
                        borderRadius: "5px",
                        padding: "4px 6px 4px 8px",
                        marginLeft: "-8px",
                        borderLeft: hoveredArc === i ? `3px solid ${s.color}` : "3px solid transparent",
                        background: hoveredArc === i ? `${s.color}12` : "transparent",
                        transition: "border-color 0.18s ease, background 0.18s ease",
                      }}
                    >
                      <span
                        className="asm-legend-color-dot"
                        style={{ background: s.color }}
                      />
                      <span
                        className="asm-legend-label"
                        style={{
                          color: hoveredArc === i ? s.color : undefined,
                          fontWeight: hoveredArc === i ? 600 : undefined,
                          transition: "color 0.18s ease",
                        }}
                      >
                        {s.label}
                      </span>
                      <span
                        className="asm-legend-count"
                        style={{
                          color: hoveredArc === i ? s.color : undefined,
                          transition: "color 0.18s ease",
                        }}
                      >
                        {s.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card 3: Risk Chart-2 (Matrix Table) */}
            <div className="asm-card">
              <div className="asm-card-header">Risk Chart-2</div>
              <div className="asm-matrix-container">
                <table className="asm-matrix-table">
                  <thead>
                    <tr>
                      <th style={{ width: "26%" }} />
                      {severities.map(s => (
                        <th key={s.key} title={s.label}>
                          <span
                            style={{
                              display: "inline-block",
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: s.color,
                              marginRight: 4,
                            }}
                          />
                          {s.short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixData.map(row => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        {row.cells.map((cell, idx) => (
                          <td key={idx}>
                            {cell.text !== "·" ? (
                              <div
                                className="asm-matrix-pill"
                                style={{ background: cell.bg, color: cell.fg }}
                              >
                                {cell.text}
                              </div>
                            ) : (
                              <div className="asm-matrix-empty-dot">·</div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Bottom Card: Tabs + Data Table */}
          <div className="asm-tabs-wrapper">
            {/* Tabs Header */}
            <div className="asm-tabs-bar">
              {Object.keys(tabViews).map(key => {
                const tab = tabViews[key];
                const count = tab.id === "whois" ? 6 : tab.rows.length;
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    className={`asm-tab-btn ${isActive ? "active" : ""}`}
                    onClick={() => handleTabChange(key)}
                  >
                    <span>{tab.label}</span>
                    <span className="asm-tab-pill-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Tab Subtitle Header */}
            <div className="asm-tab-header-title">
              <span>{currentView.title}</span>
              {currentView.sub && (
                <span className="asm-tab-sub-badge">{currentView.sub}</span>
              )}
            </div>

            {/* Table Content */}
            <div className="asm-data-table-container">
              {currentView.rows.length === 0 ? (
                <div className="asm-empty-state">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--status-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <span>No records</span>
                </div>
              ) : (
                <table className="asm-data-table">
                  <thead>
                    <tr>
                      {currentView.cols.map((col, idx) => (
                        <th key={idx}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, rIdx) => (
                      <tr key={rIdx}>
                        {row.map((cell, cIdx) => (
                          <td
                            key={cIdx}
                            className={cell.isId ? "id-cell" : cell.isMono ? "mono" : ""}
                          >
                            {cell.badge ? (
                              <span className={getBadgeClass(cell.sev)}>
                                {cell.text}
                              </span>
                            ) : (
                              cell.text
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {totalRows > 0 && (
              <div className="asm-pagination-bar">
                <div className="asm-pagination-info">
                  <span>
                    Showing <b>{totalRows === 0 ? 0 : startIndex + 1}</b> to <b>{endIndex}</b> of <b>{totalRows}</b> records
                  </span>
                  <div className="asm-pagination-pagesize">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      aria-label="Rows per page"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="asm-pagination-controls">
                    <button
                      className="asm-page-nav-btn"
                      disabled={safeCurrentPage <= 1}
                      onClick={() => setCurrentPage(1)}
                      title="First Page"
                      aria-label="First Page"
                    >
                      «
                    </button>
                    <button
                      className="asm-page-nav-btn"
                      disabled={safeCurrentPage <= 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      title="Previous Page"
                      aria-label="Previous Page"
                    >
                      ‹ Prev
                    </button>

                    <div className="asm-page-numbers">
                      {getPageNumbers(safeCurrentPage, totalPages).map((p, idx) =>
                        p === "..." ? (
                          <span key={`dots-${idx}`} className="asm-page-dots">…</span>
                        ) : (
                          <button
                            key={p}
                            className={`asm-page-num-btn ${safeCurrentPage === p ? "active" : ""}`}
                            onClick={() => setCurrentPage(p)}
                            aria-label={`Page ${p}`}
                          >
                            {p}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="asm-page-nav-btn"
                      disabled={safeCurrentPage >= totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      title="Next Page"
                      aria-label="Next Page"
                    >
                      Next ›
                    </button>
                    <button
                      className="asm-page-nav-btn"
                      disabled={safeCurrentPage >= totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      title="Last Page"
                      aria-label="Last Page"
                    >
                      »
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </React.Fragment>
  );
};

export default Dashboard2;
